/**
 * KAN-2070 — „Vyrobit na vlastní riziko" v bloku „Co s tím".
 *
 * Nabízí se jen u nálezu, kterému backend dal volbu ``own_risk``
 * (limit v režimu „rozhodne zákazník", KAN-2069) — a jen když žádný jiný
 * nález nezastavuje tvrdě (ten souhlas nepustí). Klik ukáže upozornění;
 * potvrzení pošle PŘESNĚ ten text, který zákazník viděl, a backend ho uloží
 * k položce (kdo, kdy, nálezy, text).
 */
import { Button } from "@/ingot";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import {
  type ManufacturabilityWarning,
  useAcceptManufacturabilityRisk,
} from "@/api/queries/quotes";
import { renderMessage } from "@/components/WarningsPanel";

/** Lze riziko převzít? Nález s volbou ``own_risk`` a žádný tvrdý blok vedle. */
export function ownRiskOffered(warnings: readonly ManufacturabilityWarning[]): boolean {
  const offered = warnings.some((w) => (w.remedies ?? []).some((r) => r.kind === "own_risk"));
  const hardBlock = warnings.some((w) => w.params?.severity === "block");
  return offered && !hardBlock;
}

export function OwnRiskAction({
  quoteSlug,
  lang,
  warnings,
  material,
  dimensions,
}: {
  quoteSlug: string;
  lang: string;
  warnings: readonly ManufacturabilityWarning[];
  material: string | null;
  dimensions: Record<string, unknown>;
}): JSX.Element | null {
  const { t } = useTranslation("quote");
  const accept = useAcceptManufacturabilityRisk(quoteSlug);
  const [confirming, setConfirming] = useState(false);

  if (!ownRiskOffered(warnings)) return null;

  const findings = warnings
    .filter((w) => w.params?.severity === "customer_decides")
    .map((w) => renderMessage(w, t, lang));
  const title = t("ownRisk.title");
  const intro = t("ownRisk.confirmIntro");
  const outro = t("ownRisk.confirmOutro");
  // Exactly what the customer reads in the dialog — stored with the consent.
  const text = [title, intro, ...findings.map((f) => `• ${f}`), outro].join("\n");

  if (!confirming) {
    return (
      <Button
        variant="ghost"
        size="md"
        onClick={() => setConfirming(true)}
        data-testid="quote-stop-own-risk"
      >
        {t("ownRisk.button")}
      </Button>
    );
  }

  return (
    <div
      className="mt-2 w-full rounded-md border border-warn-border bg-warn-bg p-3 text-sm text-ink"
      role="alertdialog"
      aria-label={title}
      data-testid="quote-stop-own-risk-confirm"
    >
      <p className="font-medium">{title}</p>
      <p className="mt-1">{intro}</p>
      <ul className="mt-1 list-disc pl-5">
        {findings.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      <p className="mt-1">{outro}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          variant="accent"
          size="md"
          disabled={accept.isPending}
          onClick={() =>
            accept.mutate({ material, dimensions, text, locale: lang })
          }
          data-testid="quote-stop-own-risk-accept"
        >
          {t("ownRisk.accept")}
        </Button>
        <Button
          variant="ghost"
          size="md"
          disabled={accept.isPending}
          onClick={() => setConfirming(false)}
          data-testid="quote-stop-own-risk-cancel"
        >
          {t("ownRisk.cancel")}
        </Button>
      </div>
      {accept.isError && (
        <p className="mt-2 text-[12px] text-danger" role="alert">
          {t("ownRisk.failed")}
        </p>
      )}
    </div>
  );
}
